import { ApiProvider, ProviderType } from '@/types';

export interface ApiTestResult {
  success: boolean;
  latency?: number;
  error?: string;
  models?: string[];
  providerInfo?: {
    name: string;
    version?: string;
    capabilities?: string[];
  };
}

export interface ModelListResponse {
  object: string;
  data: Array<{
    id: string;
    object: string;
    created: number;
    owned_by: string;
  }>;
}

export class ApiService {
  /**
   * 测试 API 连通性并获取模型列表
   */
  static async testConnection(provider: Pick<ApiProvider, 'baseUrl' | 'apiKey' | 'providerType'>): Promise<ApiTestResult> {
    const startTime = Date.now();
    
    try {
      const { modelsEndpoint } = this.getEndpoints(provider.baseUrl, provider.providerType);
      
      const headers = this.getHeaders(provider.apiKey, provider.providerType);
      
      const response = await fetch(modelsEndpoint, {
        method: 'GET',
        headers,
        signal: AbortSignal.timeout(10000), // 10秒超时
      });

      const latency = Date.now() - startTime;

      if (!response.ok) {
        // 尝试其他可能的端点
        return await this.tryAlternativeEndpoints(provider, startTime);
      }

      const data = await response.json();
      const models = this.extractModels(data, provider.providerType);

      return {
        success: true,
        latency,
        models,
        providerInfo: this.extractProviderInfo(data, provider.providerType),
      };
    } catch (error) {
      return {
        success: false,
        latency: Date.now() - startTime,
        error: error instanceof Error ? error.message : '未知错误',
      };
    }
  }

  /**
   * 发送聊天消息
   */
  static async sendMessage(
    provider: Pick<ApiProvider, 'baseUrl' | 'apiKey' | 'providerType'>,
    model: string,
    messages: Array<{ role: string; content: string }>,
    config?: {
      temperature?: number;
      maxTokens?: number;
      stream?: boolean;
    }
  ): Promise<any> {
    const { chatEndpoint } = this.getEndpoints(provider.baseUrl, provider.providerType);
    const headers = this.getHeaders(provider.apiKey, provider.providerType);
    
    const body = this.buildRequestBody(provider.providerType, model, messages, config);

    const response = await fetch(chatEndpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(30000), // 30秒超时
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API 请求失败: ${response.status} ${response.statusText}\n${errorText}`);
    }

    return await response.json();
  }

  /**
   * 获取端点信息
   */
  private static getEndpoints(baseUrl: string, providerType: ProviderType) {
    const url = new URL(baseUrl);
    // basePath变量用于未来扩展
    // const basePath = url.pathname.replace(/\/$/, '');

    switch (providerType) {
      case ProviderType.OPENAI:
        return {
          chatEndpoint: `${baseUrl}/chat/completions`,
          modelsEndpoint: `${baseUrl}/models`,
          healthEndpoint: `${baseUrl}/models`,
        };
      case ProviderType.ANTHROPIC:
        return {
          chatEndpoint: `${baseUrl}/v1/messages`,
          modelsEndpoint: `${baseUrl}/v1/models`,
          healthEndpoint: `${baseUrl}/v1/models`,
        };
      case ProviderType.GOOGLE_AI:
        return {
          chatEndpoint: `${baseUrl}/v1/models/${this.getDefaultModel(providerType)}:generateContent`,
          modelsEndpoint: `${baseUrl}/v1/models`,
          healthEndpoint: `${baseUrl}/v1/models`,
        };
      case ProviderType.AZURE_OPENAI:
        return {
          chatEndpoint: `${baseUrl}/chat/completions?api-version=2023-12-01-preview`,
          modelsEndpoint: `${baseUrl}/models?api-version=2023-12-01-preview`,
          healthEndpoint: `${baseUrl}/models?api-version=2023-12-01-preview`,
        };
      default:
        // 自定义 API，尝试常见端点
        return {
          chatEndpoint: `${baseUrl}/chat/completions`,
          modelsEndpoint: `${baseUrl}/models`,
          healthEndpoint: `${baseUrl}/health`,
        };
    }
  }

  /**
   * 尝试其他可能的端点
   */
  private static async tryAlternativeEndpoints(
    provider: Pick<ApiProvider, 'baseUrl' | 'apiKey' | 'providerType'>,
    startTime: number
  ): Promise<ApiTestResult> {
    const endpoints = [
      `${provider.baseUrl}/v1/models`,
      `${provider.baseUrl}/models`,
      `${provider.baseUrl}/api/models`,
      `${provider.baseUrl}/openai/models`,
    ];

    for (const endpoint of endpoints) {
      try {
        const headers = this.getHeaders(provider.apiKey, provider.providerType);
        const response = await fetch(endpoint, {
          method: 'GET',
          headers,
          signal: AbortSignal.timeout(5000),
        });

        if (response.ok) {
          const data = await response.json();
          const models = this.extractModels(data, provider.providerType);
          
          return {
            success: true,
            latency: Date.now() - startTime,
            models,
            providerInfo: this.extractProviderInfo(data, provider.providerType),
          };
        }
      } catch {
        // 继续尝试下一个端点
        continue;
      }
    }

    return {
      success: false,
      latency: Date.now() - startTime,
      error: '无法连接到 API 端点，请检查 URL 和密钥',
    };
  }

  /**
   * 获取请求头
   */
  private static getHeaders(apiKey: string, providerType: ProviderType): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    switch (providerType) {
      case ProviderType.OPENAI:
      case ProviderType.CUSTOM:
        headers['Authorization'] = `Bearer ${apiKey}`;
        break;
      case ProviderType.ANTHROPIC:
        headers['x-api-key'] = apiKey;
        headers['anthropic-version'] = '2023-06-01';
        break;
      case ProviderType.GOOGLE_AI:
        headers['Authorization'] = `Bearer ${apiKey}`;
        break;
      case ProviderType.AZURE_OPENAI:
        headers['api-key'] = apiKey;
        break;
    }

    return headers;
  }

  /**
   * 构建请求体
   */
  private static buildRequestBody(
    providerType: ProviderType,
    model: string,
    messages: Array<{ role: string; content: string }>,
    config?: {
      temperature?: number;
      maxTokens?: number;
      stream?: boolean;
    }
  ): any {
    const baseBody: any = {
      model,
      messages,
      temperature: config?.temperature ?? 0.7,
      max_tokens: config?.maxTokens ?? 1000,
      stream: config?.stream ?? false,
    };

    switch (providerType) {
      case ProviderType.ANTHROPIC:
        return {
          model,
          max_tokens: config?.maxTokens ?? 1000,
          messages: messages.map(msg => ({
            role: msg.role,
            content: [{ type: 'text', text: msg.content }],
          })),
        };
      case ProviderType.GOOGLE_AI:
        return {
          contents: messages.map(msg => ({
            role: msg.role === 'assistant' ? 'model' : 'user',
            parts: [{ text: msg.content }],
          })),
          generationConfig: {
            temperature: config?.temperature ?? 0.7,
            maxOutputTokens: config?.maxTokens ?? 1000,
          },
        };
      case ProviderType.AZURE_OPENAI:
        return {
          messages,
          temperature: config?.temperature ?? 0.7,
          max_tokens: config?.maxTokens ?? 1000,
        };
      default:
        return baseBody;
    }
  }

  /**
   * 从响应中提取模型列表
   */
  private static extractModels(data: any, providerType: ProviderType): string[] {
    try {
      switch (providerType) {
        case ProviderType.OPENAI:
        case ProviderType.AZURE_OPENAI:
        case ProviderType.CUSTOM:
          if (data.data && Array.isArray(data.data)) {
            return data.data.map((model: any) => model.id).filter(Boolean);
          }
          break;
        case ProviderType.ANTHROPIC:
          if (data.models && Array.isArray(data.models)) {
            return data.models.map((model: any) => model.id).filter(Boolean);
          }
          break;
        case ProviderType.GOOGLE_AI:
          if (data.models && Array.isArray(data.models)) {
            return data.models.map((model: any) => model.name.split('/').pop()).filter(Boolean);
          }
          break;
      }

      // 通用提取逻辑
      if (Array.isArray(data)) {
        return data.map(item => item.id || item.name || item.model).filter(Boolean);
      }

      // 尝试从对象中提取
      const models: string[] = [];
      const extractFromObject = (obj: any) => {
        if (typeof obj === 'object' && obj !== null) {
          for (const [key, value] of Object.entries(obj)) {
            if (key.toLowerCase().includes('model') && typeof value === 'string') {
              models.push(value);
            } else if (Array.isArray(value)) {
              for (const item of value) {
                if (typeof item === 'string' && item.length > 3) {
                  models.push(item);
                } else if (typeof item === 'object' && item.id) {
                  models.push(item.id);
                }
              }
            } else if (typeof value === 'object') {
              extractFromObject(value);
            }
          }
        }
      };

      extractFromObject(data);
      return [...new Set(models)]; // 去重
    } catch {
      return [];
    }
  }

  /**
   * 提取供应商信息
   */
  private static extractProviderInfo(data: any, providerType: ProviderType): {
    name: string;
    version?: string;
    capabilities?: string[];
  } {
    const info = {
      name: providerType,
      version: undefined as string | undefined,
      capabilities: [] as string[],
    };

    try {
      // 尝试从响应头或数据中提取版本信息
      if (data.version) {
        info.version = data.version;
      }

      // 根据供应商类型添加能力
      switch (providerType) {
        case ProviderType.OPENAI:
          info.capabilities = ['chat', 'completions', 'embeddings', 'fine-tuning'];
          break;
        case ProviderType.ANTHROPIC:
          info.capabilities = ['chat', 'messages', 'streaming'];
          break;
        case ProviderType.GOOGLE_AI:
          info.capabilities = ['generateContent', 'multimodal', 'streaming'];
          break;
        case ProviderType.AZURE_OPENAI:
          info.capabilities = ['chat', 'completions', 'embeddings'];
          break;
      }
    } catch {
      // 忽略错误
    }

    return info;
  }

  /**
   * 获取默认模型
   */
  private static getDefaultModel(providerType: ProviderType): string {
    switch (providerType) {
      case ProviderType.OPENAI:
        return 'gpt-3.5-turbo';
      case ProviderType.ANTHROPIC:
        return 'claude-3-haiku-20240307';
      case ProviderType.GOOGLE_AI:
        return 'gemini-pro';
      case ProviderType.AZURE_OPENAI:
        return 'gpt-35-turbo';
      default:
        return 'default';
    }
  }

  /**
   * 检查 API 密钥格式
   */
  static validateApiKeyFormat(apiKey: string, providerType?: ProviderType): boolean {
    if (!apiKey || apiKey.length < 10) return false;

    // 根据供应商类型使用特定的检查
    if (providerType) {
      switch (providerType) {
        case ProviderType.OPENAI:
          return /^sk-[a-zA-Z0-9]{20,}$/.test(apiKey);
        case ProviderType.ANTHROPIC:
          return /^sk-ant-[a-zA-Z0-9]{20,}$/.test(apiKey);
        case ProviderType.GOOGLE_AI:
          return /^[A-Za-z0-9+/]{20,}={0,2}$/.test(apiKey);
        default:
          // 通用检查
          break;
      }
    }

    // 通用检查
    const patterns = [
      /^sk-[a-zA-Z0-9]{20,}$/, // OpenAI
      /^sk-ant-[a-zA-Z0-9]{20,}$/, // Anthropic
      /^[A-Za-z0-9+/]{20,}={0,2}$/, // Base64
      /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/, // UUID
      /^[a-zA-Z0-9_\-\.]{20,}$/, // 通用
    ];

    for (const pattern of patterns) {
      if (pattern.test(apiKey)) {
        return true;
      }
    }

    return false;
  }

  /**
   * 检查 URL 格式
   */
  static validateUrlFormat(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 批量测试多个供应商
   */
  static async batchTestConnections(
    providers: Array<Pick<ApiProvider, 'id' | 'baseUrl' | 'apiKey' | 'providerType' | 'name'>>
  ): Promise<Record<string, ApiTestResult>> {
    const results: Record<string, ApiTestResult> = {};
    
    // 并行测试，但限制并发数
    const batchSize = 3;
    for (let i = 0; i < providers.length; i += batchSize) {
      const batch = providers.slice(i, i + batchSize);
      const batchPromises = batch.map(async (provider) => {
        const result = await this.testConnection(provider);
        results[provider.id] = result;
      });
      
      await Promise.all(batchPromises);
    }
    
    return results;
  }
}